import React, {useState, useEffect, useContext, useMemo, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {Button, Form, Alert} from 'react-bootstrap';
import {Form as FormType} from '../types';
import {apiService} from '../services/api';
import {ConfirmModal} from '../components/ConfirmModal';
import {FormFieldRenderer, ToastNotification, SelectModal, Breadcrumb} from '../components';
import {useFilters} from '../contexts';
import {useFormFields, useSelectModal, useToast, useFormNavigation, useIncomingNotice, leaveNotice} from '../hooks';
import {AppContext} from '../contexts/AppContext';
import {handleMutationError, handleMutationSuccess} from '../utils/formMutation';
import {processFormData} from '../utils/formDataProcessing';
import {buildContextFields, buildCreateFormInitialData} from '../utils/formInitialization';
import {applyRowContextFilters, screenRequestFilters} from '../utils/contextFilters';
import {resolveAfterSave} from '../utils/afterSaveNavigation';
import {appScreenPath} from '../utils/screenPath';
import {gpsCoordinateOf, missingRequiredPositions} from '../utils/deviceLocation';
import {useDevicePosition} from '../hooks/useDevicePosition';
import {canSaveItself, fieldsToFillIn, missingContextValues} from '../utils/formSelfSave';
import {readWorkflowMessage, WorkflowMessage} from '../utils/workflowResult';

const NO_FIELDS: FormType['fields'] = [];

// History entries whose form has already saved itself: coming back to one with
// Back or Forward must not create the record again. A page load starts empty.
const selfSavedEntries = new Set<string>();

interface CreateSimpleFormPageProps {
    form: FormType;
    appId: string;
    embedded?: boolean;
    onSuccess?: (data?: any) => void;
    /** Starting values handed in by the caller (an embedded modal has no navigation state). */
    prefill?: Record<string, any>;
    /** Fields the person may not change here - shown, but disabled. */
    lockedFields?: string[];
}

export const CreateSimpleFormPage: React.FC<CreateSimpleFormPageProps> = ({
                                                                              form,
                                                                              appId,
                                                                              embedded = false,
                                                                              onSuccess,
                                                                              prefill: prefillProp,
                                                                              lockedFields = [],
                                                                          }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const {selectedApplication} = useContext(AppContext);
    const screen = selectedApplication?.screens?.find((s: any) => s.id === form.screenId);
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const {getFilter, setFilter, removeFilter} = useFilters();
    const {toasts, showToast, showError, hideToast} = useToast();
    useIncomingNotice(showToast);

    const {formData, displayValues, rawValues, setFormData, setDisplayValues, setRawValues, handleInputChange} = useFormFields();
    const {selectModalConfig, openSelectModal, closeSelectModal} = useSelectModal();
    const [contextFields, setContextFields] = useState<Record<string, any>>({});
    // The form whose starting values are in formData; before that it is still empty.
    const [startedFor, setStartedFor] = useState<string | null>(null);

    const hasData =Object.keys(formData).some(key => {
        if (contextFields[key] !== undefined) {
            return false;
        }
        // The device fills a GPS field in by itself - that is no unsaved work.
        if (form.fields.some(field => field.code === key && gpsCoordinateOf(field))) {
            return false;
        }
        const val = formData[key];
        if (val === '' || val === null || val === undefined) {
            return false;
        }
        if (val === false) {
            return false;
        }
        return !(Array.isArray(val) && val.length === 0);

    });

    const [redirectPath, setRedirectPath] = useState<{path: string, rowData: any, notice?: WorkflowMessage} | null>(null);
    const {blocker, navigateBack, handleConfirmNavigation, handleCancelNavigation} = useFormNavigation({
        hasUnsavedChanges: hasData && !redirectPath,
        enabled: !embedded
    });

    useEffect(() => {
        if (redirectPath) {
            navigate(redirectPath.path, {
                replace: true,
                state: {
                    rowData: redirectPath.rowData,
                    ...(redirectPath.notice ? {notice: redirectPath.notice} : {}),
                },
            });
        }
    }, [redirectPath, navigate]);

    useEffect(() => {
        if (!embedded) {
            const appName = selectedApplication?.displayName || selectedApplication?.name || 'Flowy';
            document.title = `${appName} | ${form.displayName}`;
            return () => {
                document.title = 'Flowy';
            };
        }
    }, [form, selectedApplication, embedded]);

    // What the form starts with, read once per form.
    const startingValues = useMemo(() => {
        const initialData = buildCreateFormInitialData(form.fields, getFilter);

        // A workflow redirect can hand the form its starting values; they win
        // over defaults but are one-shot - they live in navigation state only.
        const prefill = {...((location.state as any)?.prefill ?? {}), ...(prefillProp ?? {})};
        const known = Object.fromEntries(
            Object.entries(prefill).filter(([code]) => form.fields.some(field => field.code === code)),
        );

        return {...initialData, ...known};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.id]);

    // Required values the form was opened without. Nobody can fill them in here
    // and the server refuses the record without them, so the form is not offered.
    const missingFromContext = missingContextValues(form.fields, startingValues);
    const blocked = missingFromContext.length > 0;

    useEffect(() => {
        setContextFields(buildContextFields(form.fields, getFilter));
        setFormData(startingValues);
        setStartedFor(form.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.id]);

    // The position of the device goes into the form's GPS fields while the
    // record is being created; it arrives later than the starting values above.
    // Said only after an attempt to send, and only while the position is still missing.
    const [sendRefusedForPosition, setSendRefusedForPosition] = useState(false);
    const {locating, locationFailure, readLocation} = useDevicePosition(
        blocked ? NO_FIELDS : form.fields,
        form.id,
        values => setFormData(prev => ({...prev, ...values})),
    );

    const nothingToFillIn = fieldsToFillIn(form.fields).length === 0;

    const createMutation = useMutation({
        mutationFn: (data: Record<string, any>) => {
            return form.kind === 'dynamic'
                ? apiService.createDynamicFormData(form.id, data)
                : apiService.createFormData(form.id, data);
        },
        onSuccess: async (response) => {
            if (!response.success) {
                handleMutationError({response, setFieldErrors, setError, showError});
                return;
            }

            handleMutationSuccess({setFieldErrors, setError});
            const newRecord = response.data?.data || response.data;
            // The table's CREATE workflow may answer with its own notice next to
            // the record (a plain string or text + type); without one the
            // generic confirmation is shown.
            const created: WorkflowMessage = readWorkflowMessage(newRecord) ?? {text: 'Úspešne vytvorené', type: 'success'};

            if (embedded && onSuccess) {
                showToast(created.text, created.type);
                onSuccess(newRecord);
            } else {
                queryClient.invalidateQueries({queryKey: ["screenData"]});

                const afterCreate = resolveAfterSave(form.afterCreate, {
                    app: selectedApplication,
                    appId,
                    recordId: newRecord?.id,
                    currentFormId: form.id,
                });

                // A form with nothing to fill in is left the moment it is saved,
                // so its message is shown by the page the person lands on.
                const carried = nothingToFillIn && !embedded && afterCreate.kind !== 'stay';
                if (!carried) {
                    showToast(created.text, created.type);
                }

                if (afterCreate.kind === 'navigate') {
                    // Simulate clicking the new record's row in the screen: form
                    // field codes can differ from the screen's column codes, so
                    // fetch the record through the screen data endpoint and save
                    // context filters from that row — embedded sub-screens and
                    // context-bound fields on the target depend on them. A record
                    // the screen does not list (its filter) is no error here.
                    let screenRow: any = null;
                    if (screen?.id && newRecord?.id) {
                        try {
                            const rowResponse = await apiService.getScreenRecord(screen.id, newRecord.id, screenRequestFilters(screen, getFilter), false);
                            screenRow = rowResponse.success ? rowResponse.data?.data : null;
                        } catch {
                            // record fetch failed — fall back to screen context only below
                        }
                        if (screenRow?.id) {
                            applyRowContextFilters(screen, screenRow, setFilter, removeFilter);
                        } else if (screen.contextCode) {
                            setFilter(screen.contextCode, String(newRecord.id));
                        }
                    }
                    setRedirectPath({
                        path: afterCreate.path,
                        rowData: screenRow?.id ? screenRow : newRecord,
                        notice: carried ? created : undefined,
                    });
                } else if (afterCreate.kind === 'stay') {
                    // Pointing the form at itself means "keep entering records".
                    setFormData({});
                } else if (nothingToFillIn && location.key === 'default') {
                    // Opened straight from a link (a QR code): there is no page to
                    // go back to and nothing to do here, so go where the application starts.
                    const mainScreenId = selectedApplication?.defaultScreenId;
                    setRedirectPath({
                        path: selectedApplication && mainScreenId ? appScreenPath(selectedApplication, mainScreenId) : '/',
                        rowData: null,
                        notice: carried ? created : undefined,
                    });
                } else {
                    setFormData({});
                    if (carried) {
                        leaveNotice(created.text, created.type);
                    }
                    navigateBack();
                }
            }
        },
        onError: (error: any) => {
            setError(error.message || 'Chyba pri ukladaní');
            showError(error.message || 'Chyba pri ukladaní');
        }
    });

    // A form with nothing to fill in saves itself, once per opening. A refused
    // attempt is not repeated - the button is left to the person.
    const selfSavedFor = useRef<string | null>(null);
    useEffect(() => {
        if (selfSavedFor.current === form.id || startedFor !== form.id || !canSaveItself(form.fields, formData, locating)) {
            return;
        }
        const entry = `${location.key}:${form.id}`;
        if (!embedded && selfSavedEntries.has(entry)) {
            return;
        }
        selfSavedFor.current = form.id;
        selfSavedEntries.add(entry);
        createMutation.mutate(processFormData(formData, form.fields));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startedFor, formData, locating]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // GPS fields are not shown, so nothing in the form would say why it
        // cannot be saved.
        if (missingRequiredPositions(form.fields, formData).length > 0) {
            setSendRefusedForPosition(true);
            return;
        }

        const processedData = processFormData(formData, form.fields);
        createMutation.mutate(processedData);
    };

    const handleModalSelect = (row: any, displayValue: string) => {
        if (selectModalConfig) {
            handleInputChange(selectModalConfig.fieldCode, row.id);
            setDisplayValues(prev => ({
                ...prev,
                [selectModalConfig.fieldCode]: displayValue
            }));
            setRawValues(prev => ({
                ...prev,
                [selectModalConfig.fieldCode]: row
            }));
            if (fieldErrors[selectModalConfig.fieldCode]) {
                setFieldErrors(prev => ({...prev, [selectModalConfig.fieldCode]: ''}));
            }
        }
    };

    const handleFieldClear = (field: any) => {
        handleInputChange(field.code, null);
        setDisplayValues(prev => ({...prev, [field.code]: ''}));
        setRawValues(prev => {
            const next = {...prev};
            delete next[field.code];
            return next;
        });
    };

    const renderField = (field: any) => {
        if (field.context) {
            return null;
        }

        if (field.type === 'one-to-many') {
            return null;
        }

        return (
            <FormFieldRenderer
                key={field.code}
                field={field}
                value={formData[field.code] || ''}
                error={fieldErrors[field.code]}
                onChange={(value) => handleInputChange(field.code, value)}
                onErrorClear={() => setFieldErrors(prev => ({...prev, [field.code]: ''}))}
                disabled={field.editable === false || lockedFields.includes(field.code)}
                displayValue={displayValues[field.code]}
                onOpenSelectModal={(field) => openSelectModal(field, screen?.columns)}
                allScreens={selectedApplication?.screens}
                rawValue={rawValues[field.code]}
                onClear={() => handleFieldClear(field)}
                autoFocus={field.code === autoFocusFieldCode}
                autoScan={true}
                formValues={formData}
                formScreen={screen}
            />
        );
    };

    const regularFields = fieldsToFillIn(form.fields)
        .sort((a, b) => a.sortOrder - b.sortOrder);

    // The form opens ready to be filled in: the cursor goes to the first field
    // that can actually take input.
    const autoFocusFieldCode = regularFields.find(f => f.editable !== false && !lockedFields.includes(f.code))?.code;

    // What the server objects to in fields the form does not show would otherwise be seen by nobody.
    const unseenFieldErrors = form.fields
        .filter(field => fieldErrors[field.code] && !regularFields.some(shown => shown.code === field.code))
        .map(field => `${field.displayName}: ${fieldErrors[field.code]}`);

    const leaveBlocked = () => {
        // Opened straight from a link: there is no page to go back to.
        if (location.key === 'default') {
            const mainScreenId = selectedApplication?.defaultScreenId;
            navigate(selectedApplication && mainScreenId ? appScreenPath(selectedApplication, mainScreenId) : '/');
            return;
        }
        navigate(-1);
    };

    const formContent = blocked ? (
        <>
            <Alert variant="warning" data-e2e="context-missing">
                <div>
                    Chýba údaj: {missingFromContext.map(field => field.displayName).join(', ')}. Tu sa nevypĺňa –
                    formulár otvorte zo záznamu, ku ktorému patrí.
                </div>
            </Alert>
            {!embedded && (
                <div className="d-flex justify-content-end mt-3">
                    <Button variant="secondary" className="btn-pill" onClick={leaveBlocked}>
                        Späť
                    </Button>
                </div>
            )}
        </>
    ) : (
        <>
            <ToastNotification
                toasts={toasts}
                onClose={hideToast}
            />
            {error && (
                <Alert variant="danger">
                    <div>
                        {error}
                        {unseenFieldErrors.map(line => <div key={line} data-e2e="unseen-field-error">{line}</div>)}
                    </div>
                </Alert>
            )}
            {sendRefusedForPosition && missingRequiredPositions(form.fields, formData).length > 0 && (
                <Alert variant="danger" data-e2e="position-required">
                    Formulár sa nedá odoslať, kým nie je zistená poloha zariadenia.
                </Alert>
            )}
            {locating && (
                <Alert variant="info" data-e2e="locating">
                    Zisťujem polohu zariadenia. Ak sa prehliadač opýta na povolenie, potvrďte ho.
                </Alert>
            )}
            {locationFailure && (
                <Alert variant="warning" data-e2e="location-error">
                    <div>
                        {locationFailure.message}
                        <Button variant="link" size="sm" className="p-0 ms-2 align-baseline" onClick={readLocation} disabled={locating}>
                            {locationFailure.needsFreshPage ? 'Obnoviť stránku' : 'Skúsiť znova'}
                        </Button>
                        <div className="small text-muted mt-1" data-e2e="location-browser-answer">
                            Odpoveď prehliadača: {locationFailure.browserAnswer}
                        </div>
                    </div>
                </Alert>
            )}

            <Form onSubmit={handleSubmit}>
                {regularFields.map(field => renderField(field))}

                <div className="d-flex justify-content-end gap-2 mt-3">
                    {!embedded && (
                        <Button
                            variant="secondary"
                            className="btn-pill"
                            onClick={() => navigate(-1)}
                            disabled={createMutation.isPending}
                        >
                            Zrušiť
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        className="btn-pill"
                        type="submit"
                        data-e2e={`submit:${form.id}`}
                        disabled={createMutation.isPending || (nothingToFillIn && locating)}
                    >
                        {createMutation.isPending ? 'Ukladám...' : nothingToFillIn && locating ? 'Zisťujem polohu...' : 'Pridať'}
                    </Button>
                </div>
            </Form>

            {selectModalConfig && (
                <SelectModal
                    show={selectModalConfig.show}
                    onHide={closeSelectModal}
                    screenId={selectModalConfig.screenId}
                    onSelect={handleModalSelect}
                    screenData={selectModalConfig.screenData}
                    formValues={formData}
                    formScreen={screen}
                />
            )}

            {!embedded && blocker.state === "blocked" && (
                <ConfirmModal
                    show={true}
                    title="Neuložené zmeny"
                    message="Máte nevyplnené údaje. Naozaj chcete zahodiť zmeny a opustiť túto stránku?"
                    confirmText="Zahodiť zmeny"
                    cancelText="Zrušiť"
                    autoHideOnConfirm={false}
                    onConfirm={handleConfirmNavigation}
                    onHide={handleCancelNavigation}
                />
            )}
        </>
    );

    if (embedded) {
        return formContent;
    }

    return (
        <>
            <Breadcrumb currentLabel={form.displayName} appId={appId} />
            <div className="page-body">
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-body">
                            {formContent}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
